import { useMutation } from '@apollo/client/react';
import { CREATE_STATEMENT_PDF_MUTATION } from '../../integrations/allkort/graphql/gql/StatementPdfMutation';
import {
	CreateStatementPdfResponse
} from '../../integrations/allkort/graphql/interface/StatementPdf';

/**
 * useCreateStatementPdf
 * 
 * Custom hook for generating a PDF for a monthly statement.
 * Returns a URL that can be used to download the statement.
 * 
 * @example
 * ```tsx
 * const { createStatementPdf, loading, error, data } = useCreateStatementPdf();
 * 
 * // Generate statement PDF
 * const handleDownload = async (statementId: number) => {
 *   try {
 *     const result = await createStatementPdf(statementId);
 *     if (result?.url) {
 *       window.open(result.url, '_blank');
 *     }
 *   } catch (err) {
 *     console.error('Failed to generate statement:', err);
 *   }
 * };
 * ```
 */
export const useCreateStatementPdf = () => {
	const [createStatementPdfMutation, { loading, error, data }] = useMutation<
		CreateStatementPdfResponse,
		{ id: number }
	>(CREATE_STATEMENT_PDF_MUTATION);

	/**
	 * Generate a PDF for a statement
	 * @param statementId - The statement ID (integer)
	 * @returns The PDF data with URL and expiration date, or null if failed
	 */
	const createStatementPdf = async (statementId: number) => {
		try {
			const result = await createStatementPdfMutation({
				variables: { id: statementId }
			});

			// Log error details if mutation failed
			if (result.error) {
				console.error('Failed to create statement PDF:', result.error.message);
				if ('graphQLErrors' in result.error && Array.isArray(result.error.graphQLErrors) && result.error.graphQLErrors.length > 0) {
					console.error('GraphQL errors:', result.error.graphQLErrors);
				}
				if ('networkError' in result.error && result.error.networkError) {
					console.error('Network error:', result.error.networkError);
				}
			}

			if (result.data?.createStatementPdf?.pdfData) {
				return result.data.createStatementPdf.pdfData;
			}
			return null;
		} catch (err) {
			console.error('Error creating statement PDF:', err);
			throw err;
		}
	};

	return {
		createStatementPdf,
		loading,
		error,
		data: data?.createStatementPdf?.pdfData ?? null
	};
};
